/**
 *  Copyright (c) 2026 TechDivision GmbH
 *  All rights reserved
 *
 *  This product includes proprietary software developed at TechDivision GmbH, Germany
 *  For more information see https://www.techdivision.com/
 *
 *  To obtain a valid license for using this software please contact us at
 *  license@techdivision.com
 *
 */

/* counter.js
   Counter element – animates each number up from 0 to its target value once it
   scrolls into view. Respects prefers-reduced-motion (jumps straight to the
   final value). */

export function initCounter() {
  var numbers = Array.prototype.slice.call(document.querySelectorAll('[data-counter-value]'));
  if (!numbers.length) return;

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function format(value, decimals, useGrouping) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        useGrouping: useGrouping
      }).format(value);
    } catch (e) {
      // fallback if Intl or the locale is unavailable
      return value.toFixed(decimals);
    }
  }

  function run(el) {
    var target = parseFloat(el.getAttribute('data-counter-value')) || 0;
    var decimals = parseInt(el.getAttribute('data-counter-decimals'), 10) || 0;
    var duration = parseInt(el.getAttribute('data-counter-duration'), 10) || 2000;
    var useGrouping = el.getAttribute('data-counter-grouping') === 'true';

    if (reduceMotion || duration <= 0) {
      el.textContent = format(target, decimals, useGrouping);
      return;
    }

    var startTime = null;

    function tick(now) {
      if (startTime === null) startTime = now;
      var progress = Math.min((now - startTime) / duration, 1);
      // easeOutCubic for a natural deceleration
      var eased = 1 - Math.pow(1 - progress, 3);
      el.textContent = format(target * eased, decimals, useGrouping);

      if (progress < 1) {
        window.requestAnimationFrame(tick);
      } else {
        el.textContent = format(target, decimals, useGrouping);
      }
    }

    window.requestAnimationFrame(tick);
  }

  if (!('IntersectionObserver' in window)) {
    numbers.forEach(run);
    return;
  }

  var observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        run(entry.target);
        observer.unobserve(entry.target);
      });
    },
    { threshold: 0.4 }
  );

  numbers.forEach(function (el) {
    observer.observe(el);
  });
}

// loaded as a module script by the section that needs it (see
// snippets/module-script.liquid), so the module initialises itself. module
// scripts are deferred, which means the DOM is complete at this point
initCounter();
